import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { StatusPill } from '@/components/app/StatusPill';
import { fmtDate } from '@/lib/format';
import { Plus, Wrench, Camera } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantMaintenance() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [lease, setLease] = useState(null);
  const [form, setForm] = useState({ issue_type: 'plumbing', priority: 'p2', title: '', description: '', photo_urls: [] });
  const load = () => api.get('/maintenance').then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/leases').then(r => { if (r.data?.length) setLease(r.data.find(l => l.status === 'active') || r.data[0]); });
  }, []);

  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, photo_urls: [...f.photo_urls, ev.target.result] }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    try {
      if (!lease) return toast.error('No active lease');
      await api.post('/maintenance', { ...form, property_id: lease.property_id, unit_id: lease.unit_id });
      toast.success('Ticket raised'); setOpen(false); setForm({ issue_type: 'plumbing', priority: 'p2', title: '', description: '', photo_urls: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader title="Maintenance requests" subtitle="Raise an issue and track resolution" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="tenant-new-ticket-button"><Plus className="h-4 w-4 mr-1" /> New request</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> Raise a request</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brief title" data-testid="ticket-title-input" /></div>
              <div><Label>Category</Label><Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="plumbing">Plumbing</SelectItem><SelectItem value="electrical">Electrical</SelectItem><SelectItem value="civil">Civil</SelectItem><SelectItem value="appliance">Appliance</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="p1">Urgent</SelectItem><SelectItem value="p2">High</SelectItem><SelectItem value="p3">Normal</SelectItem></SelectContent></Select></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's the issue? When did it start?" /></div>
              <div className="col-span-2">
                <Label>Photos (optional)</Label>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {form.photo_urls.map((p, i) => <img key={`${p}-${i}`} src={p} alt="" className="h-14 w-14 rounded-md object-cover border" />)}
                  <label className="h-14 w-14 rounded-md border border-dashed grid place-items-center text-muted-foreground cursor-pointer hover:bg-muted/40">
                    <Camera className="h-4 w-4" />
                    <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="ticket-submit-button">Submit request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      <div className="space-y-3">
        {items.map((t) => (
          <Card key={t.id} className="p-4 card-hover cursor-pointer" onClick={() => setActive(t)} data-testid={`tenant-ticket-${t.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">{t.title}</div>
                <div className="text-xs text-muted-foreground">{t.ticket_number} · {fmtDate(t.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-mono">{t.priority}</span>
                <StatusPill status={t.status} />
              </div>
            </div>
          </Card>
        ))}
        {!items.length && <Card className="p-10 text-center text-muted-foreground">No requests yet. Click “New request” to raise one.</Card>}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{active?.title}</DialogTitle></DialogHeader>
          {active && (
            <div className="text-sm space-y-3">
              <div className="text-xs text-muted-foreground font-mono">{active.ticket_number}</div>
              <div className="whitespace-pre-line">{active.description}</div>
              {active.photo_urls?.length > 0 && (
                <div className="flex gap-2 flex-wrap">{active.photo_urls.map((p, i) => <img key={`${p}-${i}`} src={p} className="h-20 w-20 rounded-md object-cover border" alt="" />)}</div>
              )}
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Status timeline</div>
                <div className="space-y-2">
                  {(active.history || []).map((h, i) => (
                    <div key={`${h.at || ''}-${h.status || h.action || ''}-${i}`} className="flex items-start gap-2">
                      <div className="h-2 w-2 rounded-full bg-[hsl(var(--role-accent))] mt-2" />
                      <div className="text-sm"><span className="font-medium capitalize">{(h.status || h.action || '').replace('_', ' ')}</span> <span className="text-muted-foreground">· {h.by} ({h.role}) · {fmtDate(h.at)}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
